import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@ascleo/ui/lib/utils";
import { stepProgressFraction, type StepProgress } from "../context";

/**
 * Footer progress of the active flow, exposed as a `progressbar` with
 * `aria-valuenow` (FC-24). Renders either the legacy counter
 * ("Schritt x / y") or, when the flow publishes `phases`, the named phases
 * with the current one highlighted and completed ones ticked (KC-02, KO-20).
 * `subStep` advances the bar inside a phase and is shown as "Teil x von y".
 * Text is at least 16px (KO-24).
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <StepProgressIndicator progress={{ current: 2, total: 3, phases: ["Anmelden", "Versicherung", "Fertig"] }} />
 * ```
 */
export function StepProgressIndicator({
  progress,
}: {
  readonly progress: StepProgress;
}) {
  const { t } = useTranslation();
  const percent = Math.round(stepProgressFraction(progress) * 100);
  const total = Math.max(progress.total, 1);
  const current = Math.min(Math.max(progress.current, 1), total);
  const phases =
    progress.phases !== undefined && progress.phases.length === progress.total
      ? progress.phases
      : null;
  const currentPhase = phases?.[current - 1];
  const subStepText =
    progress.subStep !== undefined && progress.complete !== true
      ? t("self-service.step-progress-substep", {
          current: progress.subStep.current,
          total: progress.subStep.total,
        })
      : null;
  const counterText = t("self-service.step-progress", { current, total });
  const valueText =
    progress.complete === true
      ? t("self-service.step-progress-complete")
      : currentPhase !== undefined
        ? t("self-service.step-progress-phase", {
            current,
            total,
            phase: currentPhase,
          })
        : counterText;

  return (
    <div
      data-testid="frontdesk-self-service-stepper"
      role="progressbar"
      aria-label={t("self-service.step-progress-label")}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-valuetext={subStepText ? `${valueText}, ${subStepText}` : valueText}
      className="flex min-w-0 flex-col gap-2 text-base text-muted-foreground"
    >
      <div aria-hidden className="flex min-w-0 items-baseline gap-x-3">
        {phases ? (
          <StepPhaseList
            phases={phases}
            current={current}
            complete={progress.complete === true}
          />
        ) : (
          <span className="shrink-0 text-foreground">{counterText}</span>
        )}
        {subStepText ? <span className="shrink-0">{subStepText}</span> : null}
      </div>
      <span aria-hidden className="block h-2 w-full overflow-hidden rounded-full bg-foreground-subtle">
        <i
          className="block h-full rounded-full bg-primary transition-[width] duration-[360ms]"
          style={{ width: `${percent}%` }}
        />
      </span>
    </div>
  );
}

function StepPhaseList({
  phases,
  current,
  complete,
}: {
  readonly phases: readonly string[];
  readonly current: number;
  readonly complete: boolean;
}) {
  return (
    <ol className="m-0 flex min-w-0 list-none items-center gap-x-2 p-0">
      {phases.map((phase, index) => {
        const position = index + 1;
        const done = complete || position < current;
        const active = !complete && position === current;
        return (
          <li
            key={`${position}-${phase}`}
            data-state={done ? "done" : active ? "current" : "upcoming"}
            className={cn(
              "flex min-w-0 items-center gap-1.5",
              // Narrow footers show only the current phase name.
              !active && "max-md:hidden",
              active && "font-semibold text-foreground",
              done && "text-foreground",
            )}
          >
            {index > 0 ? (
              <span className="text-muted-foreground max-md:hidden">·</span>
            ) : null}
            {done ? <Check className="size-4 shrink-0 text-success-ink" /> : null}
            <span className="truncate">{phase}</span>
          </li>
        );
      })}
    </ol>
  );
}
